import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Mail, Phone, Clock, Send, MessageCircle, Instagram } from "lucide-react";
import { SEOHead } from "@/components/SEOHead";
import { Reveal } from "@/components/Reveal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { CONTACT, FAQ } from "@/data/company";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KEBUTUHAN = [
  "Konstruksi Gedung",
  "Konstruksi Sipil & Infrastruktur",
  "Finishing & Interior",
  "Perdagangan Besar / Pengadaan",
  "Arsitektur / Desain Interior / Lanskap",
  "Lainnya",
];

const initialForm = { nama: "", perusahaan: "", email: "", telepon: "", jenis_kebutuhan: "", pesan: "" };

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Pesan Anda telah terkirim. Tim kami akan segera menghubungi Anda.");
      setForm(initialForm);
    } catch (err) {
      toast.error("Pesan gagal terkirim. Silakan coba lagi atau hubungi kami melalui WhatsApp.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <SEOHead
        title="Hubungi Kami | PT. Luminara Global Persada"
        description="Hubungi PT. Luminara Global Persada untuk kebutuhan konstruksi, arsitektur, interior, lanskap, dan pengadaan. Email, WhatsApp, dan formulir kontak resmi."
      />

      <section className="pt-40 pb-20 bg-navy-900 border-b border-white/10" data-testid="contact-header">
        <div className="container-site">
          <Reveal>
            <p className="kicker mb-4">Kontak</p>
            <h1 className="font-heading font-semibold text-4xl md:text-5xl tracking-tight max-w-3xl">
              Hubungi PT. Luminara Global Persada
            </h1>
            <p className="mt-6 max-w-2xl text-slate-400 leading-relaxed">
              Sampaikan kebutuhan proyek atau pengadaan Anda. Tim kami akan merespons pada jam operasional.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="section-pad" data-testid="contact-main-section">
        <div className="container-site grid lg:grid-cols-12 gap-14">
          <Reveal className="lg:col-span-7">
            <form
              onSubmit={handleSubmit}
              data-testid="contact-form"
              className="bg-navy-900 border border-white/10 p-8 md:p-10"
            >
              <h2 className="font-heading font-semibold text-2xl text-white">Formulir Kontak</h2>
              <p className="mt-2 text-sm text-slate-400">
                Isi data berikut dan kami akan menghubungi Anda kembali.
              </p>

              <div className="mt-8 grid sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="nama" className="text-slate-300">Nama Lengkap *</Label>
                  <Input
                    id="nama"
                    data-testid="contact-input-nama"
                    required
                    value={form.nama}
                    onChange={set("nama")}
                    placeholder="Nama Anda"
                    className="bg-navy-950 border-white/10 text-white placeholder:text-slate-600 focus-visible:ring-gold rounded-none h-12"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="perusahaan" className="text-slate-300">Perusahaan / Instansi</Label>
                  <Input
                    id="perusahaan"
                    data-testid="contact-input-perusahaan"
                    value={form.perusahaan}
                    onChange={set("perusahaan")}
                    placeholder="Nama perusahaan (opsional)"
                    className="bg-navy-950 border-white/10 text-white placeholder:text-slate-600 focus-visible:ring-gold rounded-none h-12"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-300">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="contact-input-email"
                    required
                    value={form.email}
                    onChange={set("email")}
                    placeholder="nama@perusahaan.co.id"
                    className="bg-navy-950 border-white/10 text-white placeholder:text-slate-600 focus-visible:ring-gold rounded-none h-12"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="telepon" className="text-slate-300">Nomor Telepon *</Label>
                  <Input
                    id="telepon"
                    data-testid="contact-input-telepon"
                    required
                    value={form.telepon}
                    onChange={set("telepon")}
                    placeholder="08xx xxxx xxxx"
                    className="bg-navy-950 border-white/10 text-white placeholder:text-slate-600 focus-visible:ring-gold rounded-none h-12"
                  />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label className="text-slate-300">Jenis Kebutuhan *</Label>
                  <Select
                    value={form.jenis_kebutuhan}
                    onValueChange={(v) => setForm({ ...form, jenis_kebutuhan: v })}
                    required
                  >
                    <SelectTrigger
                      data-testid="contact-select-kebutuhan"
                      className="bg-navy-950 border-white/10 text-white rounded-none h-12 focus:ring-gold"
                    >
                      <SelectValue placeholder="Pilih jenis kebutuhan" />
                    </SelectTrigger>
                    <SelectContent className="bg-navy-900 border-white/10 text-white">
                      {KEBUTUHAN.map((k) => (
                        <SelectItem key={k} value={k} data-testid={`contact-kebutuhan-${k.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                          {k}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="pesan" className="text-slate-300">Pesan *</Label>
                  <Textarea
                    id="pesan"
                    data-testid="contact-input-pesan"
                    required
                    value={form.pesan}
                    onChange={set("pesan")}
                    rows={5}
                    placeholder="Jelaskan kebutuhan proyek atau pengadaan Anda..."
                    className="bg-navy-950 border-white/10 text-white placeholder:text-slate-600 focus-visible:ring-gold rounded-none"
                  />
                </div>
              </div>

              <Button
                type="submit"
                data-testid="contact-submit-button"
                disabled={sending}
                className="mt-8 btn-gold w-full sm:w-auto disabled:opacity-60"
              >
                {sending ? "Mengirim..." : "Kirim Pesan"} <Send size={15} />
              </Button>
            </form>
          </Reveal>

          <Reveal className="lg:col-span-5" delay={0.15}>
            <div className="space-y-6">
              <div className="bg-navy-900 border border-white/10 p-8">
                <h2 className="font-heading font-semibold text-xl text-white">Informasi Kontak</h2>
                <ul className="mt-6 space-y-6 text-sm">
                  <li className="flex gap-4">
                    <span className="w-11 h-11 border border-gold/40 flex items-center justify-center text-gold shrink-0">
                      <MapPin size={17} />
                    </span>
                    <div>
                      <p className="text-slate-500 text-xs tracking-widest uppercase">Alamat Kantor</p>
                      <p className="mt-1.5 text-slate-300 leading-relaxed" data-testid="contact-address">{CONTACT.address}</p>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <span className="w-11 h-11 border border-gold/40 flex items-center justify-center text-gold shrink-0">
                      <Mail size={17} />
                    </span>
                    <div>
                      <p className="text-slate-500 text-xs tracking-widest uppercase">Email</p>
                      <a
                        href={`mailto:${CONTACT.email}`}
                        data-testid="contact-email-link"
                        className="mt-1.5 block text-slate-300 hover:text-gold transition-colors duration-300"
                      >
                        {CONTACT.email}
                      </a>
                      <a
                        href={`mailto:${CONTACT.email2}`}
                        data-testid="contact-email2-link"
                        className="mt-1 block text-slate-300 hover:text-gold transition-colors duration-300"
                      >
                        {CONTACT.email2}
                      </a>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <span className="w-11 h-11 border border-gold/40 flex items-center justify-center text-gold shrink-0">
                      <Phone size={17} />
                    </span>
                    <div>
                      <p className="text-slate-500 text-xs tracking-widest uppercase">Telepon / WhatsApp</p>
                      <a
                        href={CONTACT.waLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="contact-wa-link"
                        className="mt-1.5 block text-slate-300 hover:text-gold transition-colors duration-300"
                      >
                        {CONTACT.phoneDisplay}
                      </a>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <span className="w-11 h-11 border border-gold/40 flex items-center justify-center text-gold shrink-0">
                      <Instagram size={17} />
                    </span>
                    <div>
                      <p className="text-slate-500 text-xs tracking-widest uppercase">Instagram</p>
                      <a
                        href="https://instagram.com/luminaraglobalpersada"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="contact-instagram-link"
                        className="mt-1.5 block text-slate-300 hover:text-gold transition-colors duration-300"
                      >
                        @luminaraglobalpersada
                      </a>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <span className="w-11 h-11 border border-gold/40 flex items-center justify-center text-gold shrink-0">
                      <Clock size={17} />
                    </span>
                    <div>
                      <p className="text-slate-500 text-xs tracking-widest uppercase">Jam Operasional</p>
                      <ul className="mt-1.5 space-y-1 text-slate-300" data-testid="contact-hours">
                        {CONTACT.hours.map((h) => (
                          <li key={h.day} className="flex justify-between gap-6">
                            <span>{h.day}</span>
                            <span className="text-slate-400">{h.time}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </li>
                </ul>
                <div className="mt-8 flex flex-col sm:flex-row gap-3">
                  <a
                    href={CONTACT.waLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-wa-button"
                    className="btn-gold flex-1 !px-4"
                  >
                    <MessageCircle size={16} /> WhatsApp
                  </a>
                  <a
                    href={`mailto:${CONTACT.email}?subject=Permintaan%20Informasi%20-%20PT.%20Luminara%20Global%20Persada`}
                    data-testid="contact-email-button"
                    className="btn-outline flex-1 !px-4"
                  >
                    <Mail size={16} /> Email Kami
                  </a>
                </div>
              </div>

              <div className="border border-white/10 overflow-hidden">
                <iframe
                  title="Peta lokasi kantor PT. Luminara Global Persada, Sukmajaya, Kota Depok"
                  data-testid="contact-map"
                  src="https://maps.google.com/maps?q=Jalan%20Merdeka%20No.%2047%2C%20Abadijaya%2C%20Sukmajaya%2C%20Kota%20Depok%2C%20Jawa%20Barat%2016417&t=&z=15&ie=UTF8&iwloc=&output=embed"
                  className="w-full h-64 grayscale invert-[0.9] contrast-[0.9]"
                  loading="lazy"
                />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="section-pad bg-navy-900 border-t border-white/10" data-testid="faq-section">
        <div className="container-site max-w-4xl">
          <Reveal>
            <p className="kicker mb-4">Pertanyaan Umum</p>
            <h2 className="font-heading font-semibold text-3xl md:text-4xl tracking-tight">
              Pertanyaan seputar PT. Luminara Global Persada
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <Accordion type="single" collapsible className="mt-12" data-testid="faq-accordion">
              {FAQ.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-white/10">
                  <AccordionTrigger
                    data-testid={`faq-question-${i}`}
                    className="text-left font-heading font-medium text-white hover:text-gold hover:no-underline py-6"
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent
                    data-testid={`faq-answer-${i}`}
                    className="text-slate-400 leading-relaxed pb-6"
                  >
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>
    </>
  );
}
